import { Component } from '@theme/component';
import { debounce, prefersReducedMotion, requestIdleCallback } from '@theme/utilities';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent, DialogOpenEvent } from '@theme/dialog';

/** Same key as the previous search modal, so searches saved there carry over. */
const RECENT_SEARCHES_KEY = 'swist-recent-searches';
const RECENT_SEARCHES_LIMIT = 6;

/** Products shown per search. Shopify returns up to 10 candidates, which are re-ranked first. */
const PRODUCTS_LIMIT = 8;

const RESULTS_CACHE_LIMIT = 30;

/**
 * Türkçe karakterleri ve büyük/küçük harfi eşitler: "Kırmızı Çizgili" → "kirmizi cizgili".
 * sections/search-results.liquid'deki sıralamayla aynı kural.
 * @param {string} text
 */
function normalizeSearchText(text) {
  return text
    .toLocaleLowerCase('tr')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ı/g, 'i')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Shopify anlamsal arama yaptığı için kelimelerden sadece birini taşıyan ürünler de geliyor.
 * Tüm kelimeleri adında taşıyan ürün varsa sadece onları, yoksa en az bir kelime eşleşenleri
 * en çok eşleşen üstte olacak şekilde gösterir; eşitlikte yeni ürün önce gelir.
 * @template {{ text: string, published: number, index: number }} T
 * @param {T[]} products
 * @param {string} searchTerm
 * @returns {T[]}
 */
function rankProducts(products, searchTerm) {
  const terms = [
    ...new Set(
      normalizeSearchText(searchTerm)
        .split(' ')
        .filter((term) => term.length >= 2)
        .map((term) => (term.length > 5 && /(ler|lar)$/.test(term) ? term.slice(0, -3) : term))
    ),
  ].slice(0, 9);
  if (!terms.length) return products;

  const scored = products.map((product) => {
    const haystack = ` ${normalizeSearchText(product.text)}`;
    const score = terms.filter((term) => haystack.includes(` ${term}`)).length;
    return { product, score };
  });

  const maxScore = Math.max(...scored.map((item) => item.score));
  if (maxScore === 0) return products;

  return scored
    .filter((item) => (maxScore === terms.length ? item.score === terms.length : item.score > 0))
    .sort(
      (a, b) =>
        b.score - a.score || b.product.published - a.product.published || a.product.index - b.product.index
    )
    .map((item) => item.product);
}

/** @returns {string[]} */
function getRecentSearches() {
  try {
    const terms = JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || '[]');
    return Array.isArray(terms) ? terms.filter((term) => typeof term === 'string') : [];
  } catch {
    return [];
  }
}

/** @param {string[]} terms */
function setRecentSearches(terms) {
  try {
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(terms.slice(0, RECENT_SEARCHES_LIMIT)));
  } catch {
    // Storage can be unavailable (private mode, quota); recent searches are optional.
  }
}

/** @returns {any} The browser's speech recognition constructor, when available. */
function getSpeechRecognition() {
  const speechWindow = /** @type {any} */ (window);
  return speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition || null;
}

/** @param {EventTarget | null} target */
function isEditableTarget(target) {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable || target.closest('input, textarea, select, [contenteditable="true"]') !== null)
  );
}

/**
 * @typedef {object} PredictiveSearchRefs
 * @property {HTMLFormElement} form - The search form.
 * @property {HTMLInputElement} searchInput - The search input.
 * @property {HTMLButtonElement} resetButton - Clears the search term.
 * @property {HTMLButtonElement} [voiceButton] - Starts voice search where the browser supports it.
 * @property {HTMLElement} body - The scrollable area holding the empty state and the results.
 * @property {HTMLElement} emptyState - Shown before a term is entered.
 * @property {HTMLElement} results - Receives the rendered results.
 * @property {HTMLElement} footer - Holds the "view all results" button.
 * @property {HTMLElement} viewAllLabel - The "view all results" button label.
 * @property {HTMLElement} status - The live region for result and voice announcements.
 * @property {HTMLElement} [recentSearches] - The recent searches group.
 * @property {HTMLElement} [recentSearchesList] - The recent searches list.
 * @property {HTMLTemplateElement} [recentSearchTemplate] - The markup for one recent search.
 * @property {HTMLElement} [recentlyViewed] - The recently viewed products group.
 * @property {HTMLElement} [recentlyViewedList] - Receives the recently viewed products.
 */

/**
 * The search modal: live results while typing, recent searches, recently viewed products,
 * keyboard navigation and voice input.
 *
 * @extends {Component<PredictiveSearchRefs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = [
    'form',
    'searchInput',
    'resetButton',
    'body',
    'emptyState',
    'results',
    'footer',
    'viewAllLabel',
    'status',
  ];

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {Map<string, string>} */
  #cache = new Map();

  #recentlyViewedKey = '';
  #activeIndex = -1;
  #itemId = 0;
  #singleResultUrl = '';
  #defaultViewAllLabel = '';
  #defaultPlaceholder = '';

  /** @type {any} */
  #recognition = null;

  get dialog() {
    const dialog = this.closest('dialog-component');
    return dialog instanceof DialogComponent ? dialog : null;
  }

  get #term() {
    return this.refs.searchInput.value.trim();
  }

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const { dialog } = this;

    this.#defaultViewAllLabel = this.refs.viewAllLabel.textContent?.trim() ?? '';
    this.#defaultPlaceholder = this.refs.searchInput.placeholder;

    this.addEventListener('click', this.#handleClick, { signal });
    document.addEventListener('keydown', this.#handleShortcut, { signal });

    if (dialog) {
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
    }

    if (this.refs.voiceButton) this.refs.voiceButton.hidden = !getSpeechRecognition();

    this.#renderRecentSearches();

    // Fetch recently viewed products ahead of time so the modal opens without a layout jump.
    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => this.#loadRecentlyViewed());
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#activeFetch?.abort();
    this.#debouncedFetch.cancel();
    this.#stopVoiceSearch();
  }

  /**
   * Handles typing in the search input.
   */
  search = () => {
    const term = this.#term;

    this.refs.resetButton.hidden = term.length === 0;
    this.#setActiveIndex(-1);

    if (!term) {
      this.#showEmptyState();
      return;
    }

    this.#debouncedFetch(term);
  };

  /**
   * Handles keyboard navigation through the visible results.
   * @param {KeyboardEvent} event
   */
  onSearchKeyDown = (event) => {
    if (event.isComposing) return;

    const items = this.#items;

    switch (event.key) {
      case 'ArrowDown': {
        if (!items.length) return;
        event.preventDefault();
        this.#setActiveIndex(this.#activeIndex >= items.length - 1 ? 0 : this.#activeIndex + 1, items);
        break;
      }

      case 'ArrowUp': {
        if (!items.length) return;
        event.preventDefault();
        this.#setActiveIndex(this.#activeIndex <= 0 ? items.length - 1 : this.#activeIndex - 1, items);
        break;
      }

      case 'Enter': {
        const activeItem = items[this.#activeIndex];

        if (activeItem) {
          event.preventDefault();
          activeItem.click();
        } else if (this.#singleResultUrl && this.#term) {
          event.preventDefault();
          this.#saveRecentSearch(this.#term);
          window.location.href = this.#singleResultUrl;
        }
        break;
      }
    }
  };

  /**
   * Saves the term before the form navigates to the search page.
   * @param {SubmitEvent} event
   */
  handleSubmit = (event) => {
    const term = this.#term;

    if (!term) {
      event.preventDefault();
      this.refs.searchInput.focus();
      return;
    }

    this.#saveRecentSearch(term);
  };

  /**
   * Clears the term and returns to the empty state.
   */
  resetSearch = () => {
    this.#clear();
    this.refs.searchInput.focus();
  };

  /**
   * Copies a suggestion into the input so the shopper can keep refining it.
   * @param {MouseEvent} event
   */
  fillSuggestion = (event) => {
    const button = event.target;
    if (!(button instanceof HTMLElement) || !button.dataset.searchTerm) return;

    const { searchInput } = this.refs;
    searchInput.value = `${button.dataset.searchTerm} `;
    searchInput.focus();
    searchInput.setSelectionRange(searchInput.value.length, searchInput.value.length);
    this.search();
  };

  /**
   * @param {MouseEvent} event
   */
  removeRecentSearch = (event) => {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;

    const { searchTerm } = button.dataset;
    setRecentSearches(getRecentSearches().filter((term) => term !== searchTerm));
    this.#renderRecentSearches();
    this.refs.searchInput.focus();
  };

  clearRecentSearches = () => {
    setRecentSearches([]);
    this.#renderRecentSearches();
    this.refs.searchInput.focus();
  };

  clearRecentlyViewedProducts = () => {
    RecentlyViewed.clearProducts();
    this.#recentlyViewedKey = '';

    const { recentlyViewed, recentlyViewedList } = this.refs;
    if (recentlyViewed) recentlyViewed.hidden = true;
    recentlyViewedList?.replaceChildren();
    this.refs.searchInput.focus();
  };

  /**
   * Starts or stops voice input.
   */
  toggleVoiceSearch = () => {
    if (this.#recognition) {
      this.#recognition.stop();
      return;
    }

    const SpeechRecognition = getSpeechRecognition();
    const { voiceButton, searchInput } = this.refs;
    if (!SpeechRecognition || !voiceButton) return;

    const recognition = new SpeechRecognition();
    recognition.lang = document.documentElement.lang || 'tr';
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.addEventListener('result', (/** @type {any} */ event) => {
      searchInput.value = Array.from(event.results, (/** @type {any} */ result) => result[0]?.transcript ?? '')
        .join('')
        .trim();
      this.search();
    });
    recognition.addEventListener('end', () => this.#stopVoiceSearch(false));
    recognition.addEventListener('error', () => this.#stopVoiceSearch(false));

    this.#recognition = recognition;
    voiceButton.setAttribute('aria-pressed', 'true');
    searchInput.placeholder = this.dataset.listeningLabel || this.#defaultPlaceholder;
    this.#announce(this.dataset.listeningLabel ?? '');

    try {
      recognition.start();
    } catch {
      this.#stopVoiceSearch();
    }
  };

  #debouncedFetch = debounce((/** @type {string} */ term) => this.#fetchResults(term), 180);

  /** Visible keyboard-navigable items in the current view. */
  get #items() {
    const { results, emptyState } = this.refs;
    const container = results.hidden ? emptyState : results;

    return /** @type {HTMLElement[]} */ (
      Array.from(container.querySelectorAll('[data-search-item]')).filter(
        (item) => item instanceof HTMLElement && item.getClientRects().length > 0
      )
    );
  }

  /**
   * @param {number} index
   * @param {HTMLElement[]} [items]
   */
  #setActiveIndex(index, items = this.#items) {
    const { searchInput } = this.refs;

    for (const item of this.querySelectorAll('[data-search-item].is-active')) {
      item.classList.remove('is-active');
    }

    this.#activeIndex = index;
    const activeItem = items[index];

    if (!activeItem) {
      searchInput.removeAttribute('aria-activedescendant');
      return;
    }

    if (!activeItem.id) activeItem.id = `SearchModalItem-${++this.#itemId}`;
    activeItem.classList.add('is-active');
    searchInput.setAttribute('aria-activedescendant', activeItem.id);
    activeItem.scrollIntoView({ block: 'nearest', behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
  }

  /**
   * @param {string} term
   */
  async #fetchResults(term) {
    if (term !== this.#term) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('section_id', this.dataset.sectionId || 'predictive-search');
    url.searchParams.set('resources[type]', 'query,product,collection,page,article');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set(
      'resources[options][fields]',
      'title,product_type,variants.title,variants.sku,variants.barcode,vendor,tag,body'
    );

    const cacheKey = url.toString();
    this.#activeFetch?.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    let html = this.#cache.get(cacheKey);

    if (!html) {
      this.toggleAttribute('data-loading', true);

      try {
        const response = await fetch(url, { signal: abortController.signal });
        if (!response.ok) throw new Error(`Predictive search failed with ${response.status}`);

        html = await response.text();
        this.#cache.set(cacheKey, html);
        if (this.#cache.size > RESULTS_CACHE_LIMIT) {
          this.#cache.delete(this.#cache.keys().next().value ?? '');
        }
      } catch (error) {
        if (abortController.signal.aborted) return;
        console.error(error);
        this.#showError();
        return;
      } finally {
        if (this.#activeFetch === abortController) this.removeAttribute('data-loading');
      }
    }

    // A newer keystroke or a reset wins over this response.
    if (abortController.signal.aborted || term !== this.#term) return;

    this.#renderResults(html, term);
  }

  /**
   * @param {string} html
   * @param {string} term
   */
  #renderResults(html, term) {
    const results = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-search-results]');

    if (!(results instanceof HTMLElement)) {
      this.#showError();
      return;
    }

    const productCount = this.#rankProductResults(results, term);
    const resultCount = Number(results.dataset.resultCount) || 0;
    const otherCount = results.querySelectorAll('.search-link').length;

    this.#singleResultUrl =
      productCount === 1 && otherCount === 0
        ? results.querySelector('[data-search-product]')?.getAttribute('href') ?? ''
        : '';

    const { results: resultsContainer, emptyState, footer, viewAllLabel, searchInput, body } = this.refs;

    resultsContainer.replaceChildren(results);
    resultsContainer.hidden = false;
    emptyState.hidden = true;
    footer.hidden = resultCount === 0;
    viewAllLabel.textContent = results.dataset.viewAllLabel || this.#defaultViewAllLabel;
    searchInput.setAttribute('aria-expanded', 'true');
    body.scrollTop = 0;

    this.#activeIndex = -1;
    this.#announce(results.dataset.announcement ?? '');
  }

  /**
   * Re-ranks the product grid by how many search words each title holds.
   * @param {HTMLElement} results
   * @param {string} term
   * @returns {number} The number of products kept.
   */
  #rankProductResults(results, term) {
    const grid = results.querySelector('[data-search-product-grid]');
    if (!grid) return 0;

    const items = Array.from(grid.children, (item, index) => {
      const card = item.querySelector('[data-search-product]');

      return {
        item,
        index,
        text: card instanceof HTMLElement ? card.dataset.searchRank ?? '' : '',
        published: card instanceof HTMLElement ? Number(card.dataset.searchPublished) || 0 : 0,
      };
    });

    const ranked = rankProducts(items, term).slice(0, PRODUCTS_LIMIT);
    grid.replaceChildren(...ranked.map(({ item }) => item));

    return ranked.length;
  }

  #showError() {
    const { results, emptyState, footer, viewAllLabel, searchInput } = this.refs;
    const message = document.createElement('p');

    message.className = 'search-error';
    message.textContent = this.dataset.errorLabel ?? '';
    results.replaceChildren(message);
    results.hidden = false;
    emptyState.hidden = true;
    footer.hidden = false;
    viewAllLabel.textContent = this.#defaultViewAllLabel;
    searchInput.setAttribute('aria-expanded', 'true');
    this.#singleResultUrl = '';
    this.#announce(message.textContent);
  }

  #showEmptyState() {
    const { results, emptyState, footer, searchInput } = this.refs;

    this.#debouncedFetch.cancel();
    this.#activeFetch?.abort();
    this.#activeFetch = null;
    this.removeAttribute('data-loading');

    results.hidden = true;
    results.replaceChildren();
    emptyState.hidden = false;
    footer.hidden = true;
    searchInput.setAttribute('aria-expanded', 'false');
    this.#singleResultUrl = '';
    this.#setActiveIndex(-1);
    this.#renderRecentSearches();
  }

  #clear() {
    const { searchInput, resetButton, body } = this.refs;

    searchInput.value = '';
    resetButton.hidden = true;
    this.#showEmptyState();
    body.scrollTop = 0;
  }

  /**
   * Saves the term of any result link the shopper follows.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const { target } = event;
    if (!(target instanceof Element)) return;

    const link = target.closest('a[data-search-item]');
    if (!(link instanceof HTMLAnchorElement)) return;

    const term = link.dataset.searchTerm || this.#term;
    if (term) this.#saveRecentSearch(term);
  };

  /**
   * Opens the modal with Cmd/Ctrl + K anywhere, or with "/" outside text fields.
   * @param {KeyboardEvent} event
   */
  #handleShortcut = (event) => {
    const { dialog } = this;
    if (!dialog) return;

    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      dialog.toggleDialog();
      return;
    }

    if (event.key === '/' && !event.metaKey && !event.ctrlKey && !event.altKey && !isEditableTarget(event.target)) {
      event.preventDefault();
      dialog.showDialog();
    }
  };

  #handleDialogOpen = () => {
    this.#renderRecentSearches();
    this.#loadRecentlyViewed();
    requestAnimationFrame(() => this.refs.searchInput.focus());
  };

  #handleDialogClose = () => {
    this.#stopVoiceSearch();
    this.#clear();
  };

  /**
   * @param {string} term
   */
  #saveRecentSearch(term) {
    const cleanTerm = term.trim().replace(/\s+/g, ' ').slice(0, 80);
    if (cleanTerm.length < 2) return;

    const normalizedTerm = normalizeSearchText(cleanTerm);
    setRecentSearches([
      cleanTerm,
      ...getRecentSearches().filter((savedTerm) => normalizeSearchText(savedTerm) !== normalizedTerm),
    ]);
  }

  #renderRecentSearches() {
    const { recentSearches, recentSearchesList, recentSearchTemplate } = this.refs;
    if (!recentSearches || !recentSearchesList || !(recentSearchTemplate instanceof HTMLTemplateElement)) return;

    const terms = getRecentSearches();
    const removeLabel = recentSearchesList.dataset.removeLabel ?? '';

    recentSearchesList.replaceChildren(
      ...terms.map((term) => {
        const fragment = /** @type {DocumentFragment} */ (recentSearchTemplate.content.cloneNode(true));
        const link = fragment.querySelector('a');
        const text = fragment.querySelector('.search-suggestion__text');
        const button = fragment.querySelector('button');
        const url = new URL(Theme.routes.search_url, location.origin);

        url.searchParams.set('q', term);
        url.searchParams.set('options[prefix]', 'last');

        if (link) {
          link.href = `${url.pathname}${url.search}`;
          link.dataset.searchTerm = term;
        }
        if (text) text.textContent = term;
        if (button) {
          button.dataset.searchTerm = term;
          button.setAttribute('aria-label', removeLabel.replace('[term]', term));
        }

        return fragment;
      })
    );

    recentSearches.hidden = terms.length === 0;
  }

  async #loadRecentlyViewed() {
    const { recentlyViewed, recentlyViewedList } = this.refs;
    if (!recentlyViewed || !recentlyViewedList) return;

    const productIds = RecentlyViewed.getProducts();
    const key = productIds.join(',');
    if (key === this.#recentlyViewedKey) return;
    this.#recentlyViewedKey = key;

    if (!productIds.length) {
      recentlyViewed.hidden = true;
      recentlyViewedList.replaceChildren();
      return;
    }

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', productIds.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('section_id', this.dataset.sectionId || 'predictive-search');

    try {
      const response = await fetch(url, { signal: this.#controller?.signal });
      if (!response.ok) throw new Error(`Recently viewed products failed with ${response.status}`);

      const html = await response.text();
      if (key !== this.#recentlyViewedKey) return;

      const list = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recently-viewed]');
      const hasProducts = list instanceof HTMLElement && list.children.length > 0;

      recentlyViewedList.replaceChildren(...(hasProducts ? [list] : []));
      recentlyViewed.hidden = !hasProducts;
    } catch (error) {
      if (this.#controller?.signal.aborted) return;
      console.error(error);
      this.#recentlyViewedKey = '';
      recentlyViewed.hidden = true;
    }
  }

  /**
   * @param {boolean} [abort] - Discards the recognition instead of letting it finish.
   */
  #stopVoiceSearch(abort = true) {
    if (abort) this.#recognition?.abort();
    this.#recognition = null;

    const { voiceButton, searchInput } = this.refs;
    voiceButton?.setAttribute('aria-pressed', 'false');
    if (this.#defaultPlaceholder) searchInput.placeholder = this.#defaultPlaceholder;
  }

  /**
   * @param {string} message
   */
  #announce(message) {
    const { status } = this.refs;

    // Clearing first makes screen readers repeat an identical message.
    status.textContent = '';
    requestAnimationFrame(() => {
      status.textContent = message;
    });
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
